import { useNavigate } from "@tanstack/react-router";
import {
  Search,
  SortableCustomTable,
} from "@totalenergiescode/mobility-business-rev-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ACCOUNT_USERS_PER_PAGE } from "@/api/graphql/accounts/getAccountUsers/constants.ts";
import { useGetAccountUsers } from "@/api/graphql/accounts/getAccountUsers/hooks.ts";
import { useFormattedDate } from "@/hooks/i18n/useFormatDate/hook";

import { fromAccountUsersToColumns, fromAccountUsersToRows } from "./mappers";
import {
  StyledContainerDiv,
  StyledCurrentCountOnTotalContainerDiv,
  StyledCurrentCountOnTotalText,
  StyledDivider,
  StyledRoundedLeftContainerDiv,
  StyledSearchFieldContainerDiv,
  StyledSearchTextField,
  StyledTitleText,
} from "./styles";

export function UsersList({ accountId }: Readonly<{ accountId: string }>) {
  const { t: tCompanies } = useTranslation("companies");
  const { t: tProfiles } = useTranslation("profiles");
  const { t: tCommon } = useTranslation("common");
  const [searchTerm, setSearchTerm] = useState("");
  const {
    data: accountUsers,
    fetchNextPage,
    hasNextPage,
    totalUsers,
  } = useGetAccountUsers({ accountId, searchTerm });
  const formattedDate = useFormattedDate();
  const navigate = useNavigate();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(() => event.target.value);
  };

  return (
    <StyledContainerDiv>
      <StyledRoundedLeftContainerDiv>
        <StyledTitleText variant='title2'>
          {tCompanies("companies.list.subtitles.list")}
        </StyledTitleText>
        <StyledDivider />
        <StyledSearchFieldContainerDiv>
          <StyledSearchTextField
            dataTestId='text-field'
            onChange={handleSearchChange}
            placeholder={tCompanies("companies.list.searchPlaceholder")}
            startIcon={<Search width={18} height={18} />}
            value={searchTerm}
          />
        </StyledSearchFieldContainerDiv>

        <SortableCustomTable
          columns={fromAccountUsersToColumns({ tCompanies })}
          rows={fromAccountUsersToRows({
            accountUsers: accountUsers ?? [],
            selectedIds: new Set<string>(),
            onNavigate: (userId) => navigate({ to: `/users/${userId}` }),
            formattedDate,
            tCompanies,
            tProfiles,
          })}
          loadMoreButton={
            hasNextPage
              ? {
                  label: tCommon("common.pagination.nextPage", {
                    pageSize: ACCOUNT_USERS_PER_PAGE,
                  }),
                  onClick: () => fetchNextPage(),
                  dataTestId: "users-list_load-more-button",
                }
              : undefined
          }
          canSelectRows={false}
        />

        <StyledCurrentCountOnTotalContainerDiv>
          <StyledCurrentCountOnTotalText variant='sub3'>
            {tCompanies("companies.list.namesWithCurrentCountOnTotal.name", {
              count: accountUsers?.length ?? 0,
              total: totalUsers,
            })}
          </StyledCurrentCountOnTotalText>
        </StyledCurrentCountOnTotalContainerDiv>
      </StyledRoundedLeftContainerDiv>
    </StyledContainerDiv>
  );
}
